import { normalizeToBox, SET_PIECE_LABEL } from '../../domain/setPiece';
import type { BoxPoint, DiagramBox } from '../../domain/setPiece';
import type { SetPieceMarkerType, SetPieceRow, SetPieceScheme, SetPieceType } from '../../types';

/**
 * B-12 — the set-piece sheet's **pure decisions** (design spec §2.4; scenario 14). DOM-free so
 * the Node suite drives every arm (§12's grammar); the sheet component is glue over these plus
 * the engine's own functions (`normalizeToBox`, `placeMarker`, `cycleMarkerAt`,
 * `newSetPieceDraft`, `canCaptureSetPiece` — imported there, never re-derived here).
 */

/**
 * SES-3 — the crash-safe resume target: the **newest unfinished capture**, or `null`.
 *
 * The unfinished capture is found in the *data*, not in a session record: `draft` is a committed
 * column (`CaptureSetPiece` upserts the whole row on every marker tap), so a kill mid-markup
 * leaves exactly one row saying "this is unfinished" and entering the sheet picks it up.
 * `setPieces.listForMatch` orders by `[matchId+matchMinute]`, so the last draft is the latest
 * one; more than one draft survives only when a previous session was killed between captures,
 * and taking the newest resumes the one the user was actually working on. (The same pick A's
 * sheet makes, for the same engine — ledger: UI boilerplate over one storage contract.)
 */
export function resumeDraftB(rows: readonly SetPieceRow[]): SetPieceRow | null {
  const drafts = rows.filter((row) => row.draft);
  return drafts[drafts.length - 1] ?? null;
}

/** What one contact on the diagram means — exactly one of the three, decided from data. */
export type DiagramActionB =
  | { readonly kind: 'cycle'; readonly index: number }
  | { readonly kind: 'drop'; readonly point: BoxPoint }
  | { readonly kind: 'none' };

/**
 * §2.4 — *"≥44 pt markers placed by tap (`normalizeToBox` geometry)"*: one handler, one rule,
 * resolved from the element the finger landed on.
 *
 * A tap that lands on a placed marker (`markerIndexRaw` is that marker's `data-marker-index`)
 * **re-classifies that marker** — the engine's `MARKER_CYCLE` is its vocabulary for what a
 * marker *is*, and cycling is its answer to a repeat tap; dropping a second marker underneath
 * the first would be the sheet inventing a behavior the engine never defined. A tap anywhere
 * else **drops a new marker** at the normalized point.
 *
 * `'none'` is the refusal arm, and it exists for the same reason `normalizeToBox` returns
 * `null`: a degenerate box (an element measured before layout) or a corrupt index must be a
 * dropped tap, never a stored marker with no position and never a crash on a live surface.
 */
export function diagramActionB(
  markerIndexRaw: string | undefined,
  box: DiagramBox,
  clientX: number,
  clientY: number,
): DiagramActionB {
  if (markerIndexRaw !== undefined) {
    const index = Number(markerIndexRaw);
    if (Number.isInteger(index) && index >= 0) return { kind: 'cycle', index };
    return { kind: 'none' };
  }
  const point = normalizeToBox(box, clientX, clientY);
  if (point === null) return { kind: 'none' };
  return { kind: 'drop', point };
}

/**
 * §2.4's type picker — the four shipping kinds, split into the **two lines** the grid renders.
 *
 * The picker is a 2×2 grid whose columns are the side and whose rows are the restart, so the label
 * is split the same way: `restart` is the dominant line (the concrete noun — what just happened) and
 * `side` is the quiet one under it. Position alone would be ambiguous to a user glancing down mid-
 * match, and one wrapped sentence per button ("Defensive free kick" across two lines, four times
 * over) is slower to read than four short words. Both halves are rendered; neither is decoration.
 *
 * **This is a layout split, not a second vocabulary.** The engine's `SET_PIECE_LABEL` is what a
 * capture is called on the share card and in the match record; joining the two halves here must
 * reproduce it exactly, or B's sheet and B's record would name the same capture differently.
 * `setPieceB.test.ts` asserts the join rather than trusting the two files to be edited together.
 */
export const TYPE_LABEL_B: Readonly<Record<SetPieceType, { restart: string; side: string }>> = {
  defCorner: { restart: 'Corner', side: 'Defensive' },
  attCorner: { restart: 'Corner', side: 'Attacking' },
  defFreeKick: { restart: 'Free kick', side: 'Defensive' },
  attFreeKick: { restart: 'Free kick', side: 'Attacking' },
};

/** The kind as one phrase — the shared name, for the minute line and any read-back sentence. */
export function typeSentenceB(type: SetPieceType): string {
  return SET_PIECE_LABEL[type];
}

/** §2.4's scheme picker — `{zonal, man, hybrid}`, named in plain words. */
export const SCHEME_LABEL_B: Readonly<Record<SetPieceScheme, string>> = {
  zonal: 'Zonal',
  man: 'Man',
  hybrid: 'Hybrid',
};

/**
 * The glyph inside a rendered marker — the letter is the marker's meaning at a glance, so the
 * distinction between marker types is never hue-only (§2.3 / skill §3). A `Record` over the
 * full union: a new marker type in the engine is a compile error here, not an empty dot.
 */
export const MARKER_GLYPH_B: Readonly<Record<SetPieceMarkerType, string>> = {
  zonal: 'Z',
  man: 'M',
  nearPost: 'N',
  edgeOfBox: 'E',
  target: 'T',
  screener: 'S',
  wall: 'W',
  kickPoint: 'K',
};

/** The spoken form for `aria-label`s and the legend. */
export const MARKER_LABEL_B: Readonly<Record<SetPieceMarkerType, string>> = {
  zonal: 'Zonal',
  man: 'Man',
  nearPost: 'Near post',
  edgeOfBox: 'Edge of box',
  target: 'Delivery target',
  screener: 'Screener',
  wall: 'Wall',
  kickPoint: 'Kick point',
};
